import { useEffect, useMemo, useRef, useState } from "react";

import useLanguage from "../../hooks/useLanguage";
import useContent from "../../hooks/useContent";
import useDrawer from "../../hooks/useDrawer";
import useMediaQuery from "../../hooks/useMediaQuery";
import useToaster from "../../hooks/useToaster";
import useUserData from "../../hooks/useUserData";
import { contentFactoryMap } from "../../utils/constants";
import { notifyParent } from "../../utils/notifyParent";
import CommonPage from "../../components/common-page";
import VirtualizedTable from "../../components/virtualTable";
import { tableLayout } from "./tableLayout";
import { pickMobileColumns } from "../../components/virtualTable/mobileColumns";
import ContentSkeleton from "./skeleton";
import NewContentPopup from "./components/NewContent.popup";
import ContentTypeDrawer from "./components/ContentType.drawer";
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import SwipeVerticalIcon from "@mui/icons-material/SwipeVertical";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import {
  Checkbox,
  Popover,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

const SOURCE_OPTIONS = [
  { value: "manual", color: "#46A0E1", Icon: ArticleRoundedIcon },
  { value: "text", color: "#E1A946", Icon: AccountTreeIcon },
  { value: "content-creator", color: "#E16846", Icon: AutoAwesomeIcon },
  { value: "video2article", color: "#46E19B", Icon: OndemandVideoIcon },
  { value: "reframer", color: "#9B46E1", Icon: SwipeVerticalIcon },
  { value: "url-rewriter", color: "#2e7d32", Icon: AutoFixHighIcon },
];

const PAGE_SIZE_OPTIONS = [20, 50, 100];

// On a phone the full table overflows. Keep just Title (wraps) + actions; the
// Source column can't wrap its single-token value ("content-creator") so it
// only clips in a narrow column. Source stays available via the Source filter.
const MOBILE_COLUMN_WIDTHS = { title: 150, actions: 150 };

export default function Content() {
  const [
    {
      pages: { content: text },
    },
  ] = useLanguage();
  const { open, setElement } = useDrawer();
  const { addToaster } = useToaster();
  const {
    listContent,
    deleteContent,
    getContentById,
    recordDownload,
    setPublished,
  } = useContent();

  const isMobile = useMediaQuery();
  const columns = isMobile
    ? pickMobileColumns(tableLayout, MOBILE_COLUMN_WIDTHS)
    : tableLayout;
  const userData = useUserData();
  const isAdmin = userData?.role === "admin";
  const [content, setContent] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-indexed for MUI TablePagination
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedSources, setSelectedSources] = useState([]);
  const [availableSources, setAvailableSources] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagAnchor, setTagAnchor] = useState(null);
  const [tagQuery, setTagQuery] = useState("");
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "published" | "unpublished"
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [unpublishTarget, setUnpublishTarget] = useState(null); // { id, title } | null
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, title } | null
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [popupId, setPopupId] = useState(null);
  const fetchSeq = useRef(0);

  const filterOptions = useMemo(
    () => SOURCE_OPTIONS.filter((o) => availableSources.includes(o.value)),
    [availableSources]
  );

  const actionLabel = useMemo(
    () => (isMobile ? text.new.short : text.new.long),
    [isMobile]
  );

  // Debounce search input → reset to page 0 on change.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // --------------- Drawer Functions ---------------
  const toggleDrawer = () => open((prev) => !prev);
  const handleNewContentDrawerClick = (id) => {
    switch (id) {
      case contentFactoryMap.article:
        setPopupId(contentFactoryMap.article);
        break;
      case contentFactoryMap.contentCreator:
        setPopupId(contentFactoryMap.contentCreator);
        break;
      case contentFactoryMap.videoContentCreator:
        setPopupId(contentFactoryMap.videoContentCreator);
        break;
      case contentFactoryMap.reframer:
        setPopupId(contentFactoryMap.reframer);
        break;
    }
    toggleDrawer();
  };

  // --------------- Table Actions ---------------

  const handleSearchChange = (e) => setSearchInput(e.target.value);

  const resolveRowId = (e) =>
    e?.target?.id ||
    e?.target?.parentNode?.id ||
    e?.target?.parentNode?.parentNode?.id ||
    e?.target?.parentNode?.parentNode?.parentNode?.id;

  const handleEdit = (e) => {
    const id = resolveRowId(e);
    if (!id) return;
    const item = content.find((c) => c.id === id);
    const path = item?.source === "reframer" ? "/reframe-editor" : "/editor";
    window.history.pushState({}, "", `${path}/${id}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const handleDownload = async (e) => {
    const id = resolveRowId(e);
    const item = content.find((c) => c.id === id);

    switch (item?.source) {
      case "reframer":
        // Reframer = preview only, no audit, no auto-publish.
        if (item?.banner) window.open(item.banner, "_blank");
        break;
      case "content-creator":
      case "video2article":
      case "url-rewriter":
      case "manual": {
        // Gated on already-published: server returns 409 otherwise.
        // Block client-side too so the file blob isn't even built when
        // the audit call is guaranteed to fail.
        if (!item?.published) {
          addToaster({ message: text.toaster.downloadRequiresPublish, severity: "warning" });
          return;
        }
        const full = await getContentById(id);
        const markdown = (full?.content || []).join("\n");
        const blob = new Blob([markdown], { type: "text/markdown" });
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `${item.title || item.id}.md`;
        a.click();
        URL.revokeObjectURL(blobUrl);
        try {
          await recordDownload(id);
          notifyParent("ITEM_DOWNLOADED", id);
        } catch {
          // toaster already raised by hook
        }
        break;
      }
      default:
        addToaster({ message: text.toaster.downloadUnavailable, severity: "info" });
    }
  };

  const handlePublishedChange = async (id, checked) => {
    if (!checked) {
      const item = content.find((c) => c.id === id);
      setUnpublishTarget({ id, title: item?.title || id });
      return; // wait for confirmation
    }
    setContent((prev) => prev.map((c) => (c.id === id ? { ...c, published: true } : c)));
    try {
      await setPublished(id, true);
      notifyParent("ITEM_APPROVED", id);
    } catch {
      setContent((prev) => prev.map((c) => (c.id === id ? { ...c, published: false } : c)));
    }
  };

  const confirmUnpublish = async () => {
    if (!unpublishTarget) return;
    const id = unpublishTarget.id;
    setUnpublishTarget(null);
    setContent((prev) => prev.map((c) => (c.id === id ? { ...c, published: false } : c)));
    try {
      await setPublished(id, false);
    } catch {
      setContent((prev) => prev.map((c) => (c.id === id ? { ...c, published: true } : c)));
    }
  };
  const handleDelete = (e) => {
    const id = resolveRowId(e);
    if (!id) return;
    const item = content.find((c) => c.id === id);
    setDeleteTarget({ id, title: item?.title || id });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { id } = deleteTarget;
    setDeleteTarget(null);
    try {
      await deleteContent(id);
      setContent((prev) => prev.filter((item) => item.id !== id));
      setTotal((prev) => Math.max(0, prev - 1));
      addToaster({
        message: text.toaster.delete,
        severity: "success",
      });
    } catch (error) {
      addToaster({
        message: error.message || text.toaster.error,
        severity: "error",
      });
    }
  };

  // --------------- Fetch Data & Startup Scripts ---------------

  const toggleSource = (value) => {
    setSelectedSources((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
    setPage(0);
  };
  const clearSources = () => {
    setSelectedSources([]);
    setPage(0);
  };

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
    setPage(0);
  };
  const tagResults = (() => {
    const q = tagQuery.trim().toLowerCase();
    const opts = q
      ? availableTags.filter((t) => t.toLowerCase().includes(q))
      : availableTags;
    return opts.slice(0, 100); // cap the rendered rows; search narrows the rest
  })();

  const fetchData = async () => {
    const seq = ++fetchSeq.current;
    try {
      setLoading(true);
      const data = await listContent({
        page: page + 1,
        pageSize,
        search: debouncedSearch,
        sources: selectedSources,
        tags: selectedTags,
        published:
          statusFilter === "published"
            ? true
            : statusFilter === "unpublished"
              ? false
              : undefined,
      });
      if (seq !== fetchSeq.current) return; // newer request superseded this one
      setContent(data?.items || []);
      setTotal(data?.total || 0);
      const avail = data?.availableSources || [];
      setAvailableSources(avail);
      // Drop selected sources that no longer exist in the (search-filtered) set.
      setSelectedSources((prev) => {
        const next = prev.filter((s) => avail.includes(s));
        return next.length === prev.length ? prev : next;
      });
      const availTags = data?.availableTags || [];
      setAvailableTags(availTags);
      setSelectedTags((prev) => {
        const next = prev.filter((t) => availTags.includes(t));
        return next.length === prev.length ? prev : next;
      });
    } catch (error) {
      if (seq !== fetchSeq.current) return;
      addToaster({
        message: error.message || text.toaster.error,
        severity: "error",
      });
    } finally {
      if (seq === fetchSeq.current) {
        setLoading(false);
        setInitialLoad(false);
      }
    }
  };

  useEffect(() => {
    setElement(
      <ContentTypeDrawer
        onClose={toggleDrawer}
        onClick={handleNewContentDrawerClick}
      />
    );
    open(false);
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, selectedSources, selectedTags, statusFilter]);

  if (initialLoad && loading) return <ContentSkeleton />;
  return (
    <CommonPage
      subtitle={text.subtitle}
      title={text.title}
      maxWidth={false}
      action={{ label: actionLabel, onClick: toggleDrawer }}
    >
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          alignItems: "center",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: "minmax(240px, 420px) 180px 180px 180px 1fr",
          },
          gridTemplateAreas: {
            xs: '"search search" "source status" "tags tags"',
            sm: '"search source tags status ."',
          },
        }}
      >
        <TextField
          name="search"
          fullWidth
          variant="outlined"
          size="small"
          placeholder={text.search.placeholder}
          value={searchInput}
          onChange={handleSearchChange}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          sx={{ gridArea: "search" }}
        />

        <Button
          variant="outlined"
          color="inherit"
          size="medium"
          disabled={filterOptions.length === 0 && selectedSources.length === 0}
          onClick={(e) => setFilterAnchor(e.currentTarget)}
          sx={{
            gridArea: "source",
            width: "100%",
            textTransform: "none",
            borderColor: "divider",
            color: "text.primary",
            fontWeight: 500,
            px: 1.5,
            "&:hover": { borderColor: "text.secondary" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
            <Badge
              color="primary"
              variant="dot"
              invisible={selectedSources.length === 0}
              overlap="circular"
            >
              <FilterListIcon fontSize="small" />
            </Badge>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
              {text.filter.source}
            </Typography>
            {selectedSources.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                · {selectedSources.length}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <ArrowDropDownIcon fontSize="small" />
          </Box>
        </Button>

        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={() => setFilterAnchor(null)}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                minWidth: 240,
                maxHeight: 380,
                borderRadius: 1.5,
                boxShadow: 6,
              },
            },
          }}
          MenuListProps={{ dense: true, sx: { py: 0.5 } }}
        >
          {filterOptions.length === 0 ? (
            <MenuItem disabled>
              <ListItemText
                primary={text.filter.empty}
                primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
              />
            </MenuItem>
          ) : (
            filterOptions.map(({ value, color, Icon }) => {
              const active = selectedSources.includes(value);
              return (
                <MenuItem
                  key={value}
                  onClick={() => toggleSource(value)}
                  sx={{
                    py: 0.75,
                    px: 1.25,
                    gap: 1,
                    borderRadius: 1,
                    mx: 0.5,
                    my: 0.25,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "auto !important" }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon sx={{ color: "#fff", fontSize: 16 }} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={text.filter.sources[value] || value}
                    primaryTypographyProps={{ variant: "body2", fontWeight: active ? 600 : 400 }}
                  />
                  {active && <CheckIcon fontSize="small" color="primary" />}
                </MenuItem>
              );
            })
          )}
          {selectedSources.length > 0 && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => {
                  clearSources();
                  setFilterAnchor(null);
                }}
                sx={{ py: 0.75, justifyContent: "center" }}
              >
                <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                  {text.filter.clear}
                </Typography>
              </MenuItem>
            </>
          )}
        </Menu>

        <Button
          variant="outlined"
          color="inherit"
          size="medium"
          disabled={availableTags.length === 0 && selectedTags.length === 0}
          onClick={(e) => setTagAnchor(e.currentTarget)}
          sx={{
            gridArea: "tags",
            width: "100%",
            textTransform: "none",
            borderColor: "divider",
            color: "text.primary",
            fontWeight: 500,
            px: 1.5,
            "&:hover": { borderColor: "text.secondary" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
            <Badge
              color="primary"
              variant="dot"
              invisible={selectedTags.length === 0}
              overlap="circular"
            >
              <SellOutlinedIcon fontSize="small" />
            </Badge>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
              {text.filter.tags}
            </Typography>
            {selectedTags.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                · {selectedTags.length}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <ArrowDropDownIcon fontSize="small" />
          </Box>
        </Button>

        <Popover
          anchorEl={tagAnchor}
          open={Boolean(tagAnchor)}
          onClose={() => {
            setTagAnchor(null);
            setTagQuery("");
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{
            paper: { sx: { mt: 0.5, width: 280, borderRadius: 1.5, boxShadow: 6 } },
          }}
        >
          <Box sx={{ p: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              variant="outlined"
              placeholder={text.filter.tagsPlaceholder}
              value={tagQuery}
              onChange={(e) => setTagQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
          <Box sx={{ maxHeight: 300, overflowY: "auto", pb: 0.5 }}>
            {tagResults.length === 0 ? (
              <MenuItem disabled>
                <ListItemText
                  primary={text.filter.tagsEmpty}
                  primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                />
              </MenuItem>
            ) : (
              tagResults.map((tag) => {
                const active = selectedTags.includes(tag);
                return (
                  <MenuItem
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    sx={{ py: 0.25, px: 1, gap: 0.5, borderRadius: 1, mx: 0.5, my: 0.25 }}
                  >
                    <Checkbox
                      edge="start"
                      checked={active}
                      size="small"
                      disableRipple
                      sx={{ p: 0.5 }}
                    />
                    <ListItemText
                      primary={tag}
                      primaryTypographyProps={{
                        variant: "body2",
                        noWrap: true,
                        fontWeight: active ? 600 : 400,
                      }}
                    />
                  </MenuItem>
                );
              })
            )}
          </Box>
          {selectedTags.length > 0 && (
            <>
              <Divider />
              <MenuItem
                onClick={() => {
                  setSelectedTags([]);
                  setPage(0);
                }}
                sx={{ py: 0.75, justifyContent: "center" }}
              >
                <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                  {text.filter.clear}
                </Typography>
              </MenuItem>
            </>
          )}
        </Popover>

        <Button
          variant="outlined"
          color="inherit"
          size="medium"
          onClick={(e) => setStatusAnchor(e.currentTarget)}
          sx={{
            gridArea: "status",
            width: "100%",
            textTransform: "none",
            borderColor: "divider",
            color: "text.primary",
            fontWeight: 500,
            px: 1.5,
            "&:hover": { borderColor: "text.secondary" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", overflow: "hidden" }}>
            <Badge
              color="primary"
              variant="dot"
              invisible={statusFilter === "all"}
              overlap="circular"
            >
              <FilterListIcon fontSize="small" />
            </Badge>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
              {statusFilter === "all"
                ? text.filter.status.label
                : text.filter.status[statusFilter]}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <ArrowDropDownIcon fontSize="small" />
          </Box>
        </Button>

        <Menu
          anchorEl={statusAnchor}
          open={Boolean(statusAnchor)}
          onClose={() => setStatusAnchor(null)}
          slotProps={{
            paper: {
              sx: { mt: 0.5, minWidth: 200, borderRadius: 1.5, boxShadow: 6 },
            },
          }}
          MenuListProps={{ dense: true, sx: { py: 0.5 } }}
        >
          {["all", "published", "unpublished"].map((value) => {
            const active = statusFilter === value;
            return (
              <MenuItem
                key={value}
                onClick={() => {
                  setStatusFilter(value);
                  setPage(0);
                  setStatusAnchor(null);
                }}
                sx={{ py: 0.75, px: 1.5, mx: 0.5, my: 0.25, borderRadius: 1 }}
              >
                <ListItemText
                  primary={text.filter.status[value]}
                  primaryTypographyProps={{ variant: "body2", fontWeight: active ? 600 : 400 }}
                />
                {active && <CheckIcon fontSize="small" color="primary" />}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>

      <VirtualizedTable
        columns={columns}
        data={content}
        disableBorder
        columnHandlers={{
          published: {
            // Only admins can flip publish state from the main table. Non-admin
            // editors (role=user|client) publish from inside the article via
            // the editor's Publish button; the table indicator stays read-only.
            onChange: isAdmin ? handlePublishedChange : undefined,
            disabled: () => !isAdmin,
            tooltipOn: isAdmin ? text.publishedTooltip.on : text.publishedTooltip.readOnly,
            tooltipOff: isAdmin ? text.publishedTooltip.off : text.publishedTooltip.readOnly,
          },
        }}
        actions={(row) => [
          <Tooltip
            key="edit-tooltip"
            title={row.source === "reframer" ? text.actions.editReframe : text.actions.edit}
            arrow
            placement="top"
          >
            <IconButton
              key="edit"
              onClick={handleEdit}
              sx={{ color: row.source === "reframer" ? "#9b46e1" : undefined }}
              color={row.source === "reframer" ? undefined : "primary"}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>,
          <Tooltip
            key="attach-tooltip"
            title={
              row.source !== "reframer" && !row.published
                ? text.actions.downloadRequiresPublish
                : text.actions.download
            }
            arrow
            placement="top"
          >
            {/* span wrapper so the tooltip still fires when the button is disabled */}
            <span>
              <IconButton
                key="Attach"
                onClick={handleDownload}
                color="info"
                disabled={row.source !== "reframer" && !row.published}
              >
                <DownloadRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>,
          <Tooltip key="delete-tooltip" title={text.actions.delete} arrow placement="top">
            <IconButton key="delete" onClick={handleDelete} color="error">
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>,
        ]}
      />
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        labelRowsPerPage={text.pagination.rowsPerPage}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} ${text.pagination.of} ${count !== -1 ? count : `${to}+`}`
        }
        getItemAriaLabel={(type) =>
          type === "next" ? text.pagination.nextPage : text.pagination.previousPage
        }
        backIconButtonProps={{ title: text.pagination.previousPage }}
        nextIconButtonProps={{ title: text.pagination.nextPage }}
        sx={{
          flexShrink: 0,
          overflow: "hidden",
          "& .MuiToolbar-root": {
            display: { xs: "grid", sm: "flex" },
            gridTemplateColumns: "auto 1fr",
            gridTemplateAreas: '"label input" "rows actions"',
            columnGap: { xs: 1, sm: 0 },
            rowGap: 0.5,
            justifyContent: "flex-end",
            flexWrap: "wrap",
            px: { xs: 1, sm: 2 },
            py: { xs: 1, sm: 0 },
            minHeight: { xs: "auto", sm: 52 },
            width: "100%",
          },
          "& .MuiTablePagination-spacer": { display: { xs: "none", sm: "block" } },
          "& .MuiTablePagination-selectLabel": {
            gridArea: { xs: "label", sm: "auto" },
            justifySelf: { xs: "start", sm: "auto" },
            alignSelf: "center",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            m: 0,
          },
          "& .MuiTablePagination-input": {
            gridArea: { xs: "input", sm: "auto" },
            justifySelf: { xs: "start", sm: "auto" },
            alignSelf: "center",
            mx: { xs: 0, sm: 1 },
            mr: { xs: 0, sm: 2 },
          },
          "& .MuiTablePagination-displayedRows": {
            gridArea: { xs: "rows", sm: "auto" },
            justifySelf: { xs: "start", sm: "auto" },
            alignSelf: "center",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            m: 0,
          },
          "& .MuiTablePagination-actions": {
            gridArea: { xs: "actions", sm: "auto" },
            justifySelf: { xs: "end", sm: "auto" },
            alignSelf: "center",
            ml: { xs: 0, sm: 2 },
          },
        }}
      />
      <NewContentPopup
        open={!!popupId}
        type={popupId}
        onClose={() => setPopupId(null)}
      />
      <Dialog
        open={!!unpublishTarget}
        onClose={() => setUnpublishTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{text.unpublishDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{text.unpublishDialog.message}</DialogContentText>
          {unpublishTarget?.title && (
            <Typography
              variant="body2"
              sx={{ mt: 1.5, fontWeight: 600, color: "text.primary" }}
            >
              {unpublishTarget.title}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnpublishTarget(null)} color="inherit">
            {text.unpublishDialog.cancel}
          </Button>
          <Button onClick={confirmUnpublish} color="error" variant="contained">
            {text.unpublishDialog.confirm}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{text.deleteDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{text.deleteDialog.message}</DialogContentText>
          {deleteTarget?.title && (
            <Typography
              variant="body2"
              sx={{ mt: 1.5, fontWeight: 600, color: "text.primary" }}
            >
              {deleteTarget.title}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} color="inherit">
            {text.deleteDialog.cancel}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {text.deleteDialog.confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </CommonPage>
  );
}
